import { Download, Smartphone, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { usePWA } from '@/hooks/usePWA';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { isMac, isSafari, showMacSafariInstallInstructions, showIOSInstallInstructions, showChromeInstallInstructions } from '@/utils/pwa';

interface PWAInstallButtonProps {
  variant?: 'button' | 'banner' | 'compact';
  className?: string;
}

export function PWAInstallButton({ variant = 'button', className }: PWAInstallButtonProps) {
  const { canInstall, isIOS, installPWA, installPrompt } = usePWA();
  const [dismissed, setDismissed] = useState(false);

  if (!canInstall || dismissed) return null;

  const handleInstall = () => {
    if (isIOS) {
      showIOSInstallInstructions();
      return;
    }

    if (isMac() && isSafari()) {
      showMacSafariInstallInstructions();
      return;
    }

    // Se houver prompt nativo (Chrome/Edge com evento disparado)
    if (installPrompt) {
      installPWA();
      return;
    }

    // Fallback para Chrome/Edge sem evento disparado (instrução manual)
    showChromeInstallInstructions();
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  // Considera manual se for iOS, Safari Mac, ou se não houver prompt nativo disponível
  const isManualInstall = isIOS || (isMac() && isSafari()) || !installPrompt;

  if (variant === 'banner') {
    return (
      <Card className={cn(
        'fixed bottom-20 left-4 right-4 z-[100] shadow-lg border-primary/20 bg-card/95 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-20 duration-500',
        'sm:bottom-auto sm:top-4 sm:left-auto sm:right-4 sm:max-w-xs sm:slide-in-from-top-4',
        className
      )}>
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 bg-primary/10 rounded-full p-1.5">
              <Smartphone className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-xs text-foreground">
                Instalar Lumo
              </h3>
              <p className="text-[10px] text-muted-foreground line-clamp-1">
                {isManualInstall
                  ? 'Adicione à tela inicial'
                  : 'Instale para melhor experiência'
                }
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleInstall}
                className="h-7 px-2 text-[10px] text-primary hover:text-primary hover:bg-primary/10"
              >
                {isManualInstall ? 'Como' : 'Instalar'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="h-7 w-7 p-0 opacity-50 hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'compact') {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleInstall}
        className={cn(
          'flex items-center gap-2 text-xs bg-primary/5 border-primary/20 hover:bg-primary/10',
          className
        )}
      >
        <Download className="h-3 w-3" />
        Instalar
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={handleInstall}
      className={cn(
        'flex items-center gap-2 bg-primary/5 border-primary/20 hover:bg-primary/10',
        className
      )}
    >
      <Download className="h-4 w-4" />
      {isManualInstall ? 'Como instalar' : 'Instalar App'}
    </Button>
  );
}